import React, { useState, useEffect, useRef } from 'react';

import { useDispatch } from 'react-redux';

interface OTPVerificationProps {
  email: string;
  onBack: () => void;
  onVerify: (otp: string) => Promise<void>;
  onSuccess: () => void;
}

export default function OTPVerification({
  email,
  onBack,
  onVerify,
  onSuccess,
}: OTPVerificationProps) {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const dispatch = useDispatch();

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) {
      value = value[0];
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Focus previous input on backspace if current input is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const newOtp = [...otp];

    pastedData.split('').forEach((char, index) => {
      if (index < 6) {
        newOtp[index] = char;
      }
    });

    setOtp(newOtp);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const otpString = otp.join('');
      await onVerify(otpString);
      // Success callback will handle redirection to root route
      onSuccess();
    } catch (error: any) {
      setError(error.message || 'Failed to verify OTP');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full flex flex-col items-center animate-[slide-up_0.5s_ease-out]">
      <div className="w-full bg-white border border-gray-100 p-8 rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-4">

        {/* Header */}
        <div className="text-center mb-8 relative z-20">
          {/* Email Icon */}
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-blue-50 flex items-center justify-center">
            <svg className="w-7 h-7 text-[#0D60E6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h2>
          <p className="text-sm text-gray-500">
            We&apos;ve sent a code to <span className="font-medium text-gray-900">{email.toLowerCase()}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="w-full space-y-6 relative z-20">
          {/* OTP Input Boxes */}
          <div className="flex justify-center gap-3">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el; }}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className={`w-12 h-14 text-center text-xl font-bold border-2 rounded-xl transition-all
                  ${digit ? 'border-[#0D60E6] bg-blue-50' : 'border-gray-200 bg-gray-50/50'}
                  focus:ring-2 focus:ring-[#0D60E6] focus:border-[#0D60E6] focus:bg-white
                  text-gray-900`}
              />
            ))}
          </div>

          {error && (
            <div className="flex items-center justify-center gap-2 text-sm text-red-500 bg-red-50 py-2 px-4 rounded-lg">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || otp.some((digit) => !digit)}
            className="w-full bg-gradient-to-r from-[#0D60E6] to-[#0A4BB5] text-white py-3.5 rounded-xl font-semibold hover:-translate-y-[2px] hover:shadow-lg hover:shadow-[#0D60E6]/30 disabled:opacity-70 disabled:from-gray-400 disabled:to-gray-500 disabled:shadow-none disabled:transform-none disabled:cursor-not-allowed transition-all duration-300"
          >
            {isLoading ? 'Verifying...' : 'Verify and Continue'}
          </button>

          {/* Resend Timer */}
          <div className="text-center text-sm">
            {resendTimer > 0 ? (
              <p className="text-gray-500">
                Resend code in <span className="font-semibold text-gray-700">{resendTimer}s</span>
              </p>
            ) : (
              <button
                type="button"
                onClick={() => setResendTimer(60)}
                className="text-[#0D60E6] hover:text-[#0A4BB5] font-semibold transition-colors duration-200"
              >
                Resend code
              </button>
            )}
          </div>

          {/* Back Button */}
          <div className="text-center pt-2">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#0D60E6] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
